'use client';

import { c as _c } from "react/compiler-runtime";
import { type ActiveWallet, createTransactionTraceId, hashDiagnosticEntity, type QueryClient, reconcileProposalEntity, type SigningDeps, type SubmissionResult, type useTurnkeySubmissionDeps } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { type DynamicTranslator, resolveApiErrorMessage } from '@/lib/errors/resolve-api-error';
import type { Network } from '@/lib/network';
import { logger } from '@/lib/telemetry/logger';
import { type BackingAccounts, trackBackTxException, trackBackTxFailure } from '@/lib/tx-engine/back-market';
import type { useSubmitTransaction } from '@/lib/tx-engine/use-submit-transaction';
import { dismissToast, showToast } from '@/lib/ui/toast';
type SubmitTxFn = ReturnType<typeof useSubmitTransaction>;
type Translator = ReturnType<typeof useTranslations>;
interface WithdrawHandlerDeps {
  activeWallet: ActiveWallet;
  proposalPubkey: string;
  /** Indexed market bindings; undefined falls back to the builder's Market RPC read. */
  backingAccounts: BackingAccounts | undefined;
  network: Network;
  shareBalanceD6Str: string;
  netReturnUsdc: number;
  submitTx: SubmitTxFn;
  signingDeps: SigningDeps;
  turnkeyDeps: ReturnType<typeof useTurnkeySubmissionDeps>;
  queryClient: QueryClient;
  invalidateWalletBalances: () => void;
  abortControllerRef: React.MutableRefObject<AbortController | null>;
  setActiveAbortController: (controller: AbortController | null) => void;
  formatBalance: (value: number) => string;
  tToasts: Translator;
  tWithdraw: Translator;
  tRoot: DynamicTranslator;
}
export function useProposalWithdraw() {
  const $ = _c(4);
  const tToasts = useTranslations("launchpad.toasts");
  const tWithdraw = useTranslations("launchpad.withdraw");
  const tRoot = useTranslations() as unknown as DynamicTranslator;
  let t0;
  if ($[0] !== tRoot || $[1] !== tToasts || $[2] !== tWithdraw) {
    t0 = deps => runWithdraw({
      ...deps,
      tToasts,
      tWithdraw,
      tRoot
    });
    $[0] = tRoot;
    $[1] = tToasts;
    $[2] = tWithdraw;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}
async function runWithdraw(deps: WithdrawHandlerDeps): Promise<void> {
  const {
    activeWallet,
    proposalPubkey,
    backingAccounts,
    network,
    shareBalanceD6Str,
    netReturnUsdc,
    submitTx,
    signingDeps,
    turnkeyDeps,
    queryClient,
    invalidateWalletBalances,
    abortControllerRef,
    setActiveAbortController,
    formatBalance,
    tToasts,
    tWithdraw,
    tRoot
  } = deps;
  const abortController = new AbortController();
  abortControllerRef.current = abortController;
  setActiveAbortController(abortController);
  const traceId = createTransactionTraceId();
  // `unback` has no outcome; 0 matches the SDK's claim/boost convention.
  const errorContext = {
    traceId,
    activeWallet,
    marketPubkey: proposalPubkey,
    outcomeIndex: 0,
    network,
    startedAt: Date.now()
  };
  let submissionStarted = false;
  let submissionResult: SubmissionResult | undefined;
  if (backingAccounts === undefined) {
    logger.warn('proposal_unback.indexed_accounts_unavailable', {
      traceId,
      proposalHash: hashDiagnosticEntity(proposalPubkey)
    });
  }
  showToast({
    id: TOAST_IDS.PROPOSAL_WITHDRAW_SUBMITTING,
    message: tToasts('withdrawSubmitting'),
    variant: 'loading',
    duration: Infinity,
    cancel: {
      label: tWithdraw('cancel'),
      onClick: (): void => {
        abortController.abort();
      }
    }
  });
  try {
    const {
      unbackMarketAndSubmit
    } = await import('@/lib/tx-engine/back-market');
    const result = await unbackMarketAndSubmit({
      submitTx: (...args) => {
        submissionStarted = true;
        return submitTx(...args);
      },
      signingDeps,
      turnkeyDeps,
      activeWallet: {
        id: activeWallet.id,
        address: activeWallet.address,
        type: activeWallet.type
      },
      traceId,
      signal: abortController.signal
    }, {
      marketPubkey: proposalPubkey,
      accounts: backingAccounts,
      burnAmountD6: shareBalanceD6Str
    });
    submissionResult = result;
    if (result.ok) {
      onEvent('proposal_withdrawn', {
        traceId,
        proposalId: proposalPubkey,
        amount: netReturnUsdc
      });
      invalidateWalletBalances();
      const proposalRefresh = await reconcileProposalEntity(queryClient, proposalPubkey).catch(() => 'rejected' as const);
      if (proposalRefresh === 'rejected' || proposalRefresh === 'error') {
        logger.warn('proposal_unback.cache_reconciliation_failed', {
          traceId,
          network,
          proposalHash: hashDiagnosticEntity(proposalPubkey),
          proposalRefresh
        });
      }
      dismissToast(TOAST_IDS.PROPOSAL_WITHDRAW_SUBMITTING);
      showToast({
        id: TOAST_IDS.PROPOSAL_WITHDRAWN,
        message: tToasts('withdrawSuccess'),
        description: formatBalance(netReturnUsdc),
        variant: 'success',
        duration: 5000
      });
    } else if (abortController.signal.aborted) {
      dismissToast(TOAST_IDS.PROPOSAL_WITHDRAW_SUBMITTING);
      showToast({
        id: TOAST_IDS.PROPOSAL_WITHDRAW_CANCELLED,
        message: tToasts('withdrawCancelled'),
        variant: 'warning',
        duration: 5000
      });
    } else {
      trackBackTxFailure('unback', errorContext, result.error);
      dismissToast(TOAST_IDS.PROPOSAL_WITHDRAW_SUBMITTING);
      showToast({
        id: TOAST_IDS.PROPOSAL_WITHDRAW_ERROR,
        message: resolveApiErrorMessage(result.error, tRoot) ?? tToasts('withdrawError'),
        variant: 'error',
        duration: Infinity,
        cancel: {
          label: tWithdraw('cancel'),
          onClick: (): void => {
            dismissToast(TOAST_IDS.PROPOSAL_WITHDRAW_ERROR);
          }
        }
      });
    }
  } catch (err: unknown) {
    if (abortController.signal.aborted) {
      dismissToast(TOAST_IDS.PROPOSAL_WITHDRAW_SUBMITTING);
      return;
    }
    logger.error('proposal_unback.exception', {
      traceId,
      errorClass: err instanceof Error ? 'Error' : typeof err
    });
    if (submissionResult === undefined) {
      trackBackTxException('unback', errorContext, err, submissionStarted);
    }
    dismissToast(TOAST_IDS.PROPOSAL_WITHDRAW_SUBMITTING);
    const errDescription = err instanceof Error ? err.message : null;
    showToast({
      id: TOAST_IDS.PROPOSAL_WITHDRAW_ERROR,
      message: resolveApiErrorMessage(err, tRoot) ?? tToasts('withdrawError'),
      ...(errDescription !== null ? {
        description: errDescription
      } : {}),
      variant: 'error',
      duration: Infinity,
      cancel: {
        label: tWithdraw('cancel'),
        onClick: (): void => {
          dismissToast(TOAST_IDS.PROPOSAL_WITHDRAW_ERROR);
        }
      }
    });
  } finally {
    if (abortControllerRef.current === abortController) {
      abortControllerRef.current = null;
      setActiveAbortController(null);
    }
  }
}